// ReportsPage — port of admin/views/reports.php.
// One-click CSV exports for the four canonical reports the WP plugin produced:
//   - Attendance (date range)
//   - Leaves (year + optional status/type filters)
//   - Leave Balances (year)
//   - Staff master
//
// XLSX exports (the newer attendance.xlsx / leaves.xlsx) are reachable from
// the Attendance + Leaves admin screens directly; here we keep the older
// CSV exports the WP reports.php exposed.

import React, { useState } from "react";
import { useToast } from "@chakra-ui/react";
import { API_BASE } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader } from "../../Components/ui";

const inputStyle = { border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)" };
const btnPrimary = { background: "var(--accent-primary)", color: "white", padding: "8px 16px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };

function thisMonthRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const to   = new Date().toISOString().slice(0, 10);
  return { from, to };
}

async function downloadCsv(url, filename, toast) {
  const token = localStorage.getItem("token");
  try {
    const res = await fetch(`${API_BASE}${url}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { msg = (await res.json()).message || msg; } catch (e) {}
      throw new Error(msg);
    }
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  } catch (e) { toast({ title: e.message, status: "error" }); }
}

export default function ReportsPage() {
  const toast = useToast();
  useShellHeader({ title: "Reports", breadcrumb: "Admin · Reports" }, []);

  const def = thisMonthRange();
  const [attFrom, setAttFrom] = useState(def.from);
  const [attTo, setAttTo]     = useState(def.to);
  const [year, setYear]       = useState(String(new Date().getFullYear()));
  const [leaveStatus, setLeaveStatus] = useState("");
  const [leaveType, setLeaveType]     = useState("");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>Reports</h2>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
          One-click CSV exports. For formatted XLSX exports use the Attendance and Leave Requests pages.
        </div>
      </div>

      <Card>
        <CardHeader title="Attendance" subtitle="One row per staff per day in the chosen range." />
        <div style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>From:</label>
          <input type="date" style={inputStyle} value={attFrom} onChange={(e) => setAttFrom(e.target.value)} />
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>To:</label>
          <input type="date" style={inputStyle} value={attTo} onChange={(e) => setAttTo(e.target.value)} />
          <button style={btnPrimary} onClick={() => downloadCsv(`/reports/attendance?from=${attFrom}&to=${attTo}`, `attendance_${attFrom}_to_${attTo}.csv`, toast)}>
            ⬇ Download CSV
          </button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Leaves" subtitle="All leave requests filtered by year + status + type." />
        <div style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>Year:</label>
          <input type="number" min="2000" max="2100" style={{ ...inputStyle, width: 100 }} value={year} onChange={(e) => setYear(e.target.value)} />
          <select style={inputStyle} value={leaveStatus} onChange={(e) => setLeaveStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select style={inputStyle} value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
            <option value="">All types</option>
            <option value="annual">Annual</option>
            <option value="sick">Sick</option>
            <option value="casual">Casual</option>
            <option value="unpaid">Unpaid</option>
          </select>
          <button style={btnPrimary} onClick={() => {
            const params = new URLSearchParams({ from: `${year}-01-01`, to: `${year}-12-31` });
            if (leaveStatus) params.append("status", leaveStatus);
            if (leaveType)   params.append("leaveType", leaveType);
            downloadCsv(`/reports/leaves?${params}`, `leaves_${year}.csv`, toast);
          }}>⬇ Download CSV</button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Leave Balances" subtitle="Year-end balance summary per staff." />
        <div style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>Year:</label>
          <input type="number" min="2000" max="2100" style={{ ...inputStyle, width: 100 }} value={year} onChange={(e) => setYear(e.target.value)} />
          <button style={btnPrimary} onClick={() => downloadCsv(`/reports/balances?year=${year}`, `leave_balances_${year}.csv`, toast)}>⬇ Download CSV</button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Staff Master" subtitle="All staff records with profile + employment fields." />
        <div style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <button style={btnPrimary} onClick={() => downloadCsv("/reports/staff", "staff_master.csv", toast)}>⬇ Download CSV</button>
        </div>
      </Card>
    </div>
  );
}
